import { saveAs } from 'file-saver'
import React, { useCallback, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

// UI
import { Box, Button, Flex, Heading, Spacer } from '@chakra-ui/react'

// Component

import { authSelector } from '@/stores/auth/authSlice'

//API
import useUser from '@/features/users/useUser'

//Constant
import { getOfferListCommonColumns } from '@/features/offers/constants/offerColumns'

import { ContributorGroupType } from '@/features/contributorGroups/constants'
import { SystemVariableType } from '@/features/systemVariables/constants'
import { camelToWords } from '@/utils/stringHelper'
import { OfferStatus } from '../constants'

import { useInitialTableState } from '@/components/Table/components/useInitialTableState'
import FullTable, { tableInitialState } from '@/components/Table/FullTable/FullTable'
import { convertTableStateToQuery } from '@/utils/tableHelper'
import { createColumnHelper } from '@tanstack/react-table'

import { RawTextCell } from '@/components/Table/components/Cell'
import { useCostCenter } from '@/features/costCenters/useCostCenter'
import { useGCKCode } from '@/features/GCKCodes/useGCKCode'
import useSystemVariable from '@/features/systemVariables/useSystemVariable'
import { createExcelBlob } from '@/utils/fileHelper'
import useOfferList from '../hooks/useOfferList'
import { FilterType } from '@/components/Table/components/Filter/TableFilter'

const OfferParticipationListPage = () => {
  const { user: currentUser } = useSelector(authSelector)
  const [searchParams] = useSearchParams()
  const location = useLocation()

  const status = searchParams.get('status')

  const initialFilter = status ? { columnFilters: [{ id: 'status', value: status.split(',') }] } : {}

  // Local State
  const [storedTableState, setStoredTableState] = useInitialTableState(`table_state:offerParticipation`, {
    initialFilter,
  })

  const query = convertTableStateToQuery(storedTableState)

  // Hooks

  const { useFetchOfferParticipations } = useOfferList()
  const { useCostCenterOptions } = useCostCenter()
  const { useGCKCodeOptions } = useGCKCode()
  const { useUserOptions } = useUser()
  const { useSystemVariableOptions } = useSystemVariable()

  const { data: offerParticipations } = useFetchOfferParticipations(query)

  const { data: allOfferParticipationResponse } = useFetchOfferParticipations({
    page: 1,
    pageSize: 0,
    filters: query.filters,
  })

  //Transformed Data
  const { data: teamOptions } = useSystemVariableOptions(SystemVariableType.TEAM, { status: 'active' })
  const { data: businessUnitOptions } = useSystemVariableOptions(SystemVariableType.BUSINESS_UNIT, { status: 'active' })
  const { data: costCenterOptions } = useCostCenterOptions({ status: 'active', labelType: 'simple' })
  const { data: GCKOptions } = useGCKCodeOptions({ status: 'active', labelType: 'detail' })

  const { data: allUserOptions } = useUserOptions({ status: 'active' })
  const { data: salesUserOptions } = useUserOptions({
    labelKey: 'fullName',
    contributorGroup: [
      ContributorGroupType.TECHNICAL_PROJECT_MANAGER,
      ContributorGroupType.SALES,
      ContributorGroupType.ENGINEER,
    ],
  })
  const { data: PMOptions } = useUserOptions({
    status: 'active',
    contributorGroup: [
      ContributorGroupType.TECHNICAL_PROJECT_MANAGER,
      ContributorGroupType.SALES,
      ContributorGroupType.ENGINEER,
    ],
  })

  const navigate = useNavigate()
  const columnHelper = createColumnHelper()

  const handleDownload = useCallback(() => {
    try {
      const offerData = allOfferParticipationResponse?.data?.map((row) => ({
        ...row,
        grossMargin: row.grossMargin ? row.grossMargin + '%' : '(-)',
      }))
      const excelBlob = createExcelBlob(offerData ?? [])
      saveAs(excelBlob, 'offer participation.xlsx')
    } catch (err) {
      console.log(err)
    }
  }, [allOfferParticipationResponse?.data])

  const handleGotoOffer = (row) => {
    if (!row?.original?._id) {
      return
    }
    navigate(`/offers/${row?.original?._id}`)
  }

  const defaultColumns = [
    ...getOfferListCommonColumns({
      salesUserOptions,
      creatorOptions: allUserOptions,
      teamOptions,
      businessUnitOptions,
      PMOptions,
      costCenterOptions,
      GCKOptions,
    }),
    columnHelper.accessor('newOrRenewal', {
      header: () => <span>New / Renewal</span>,

      meta: {
        filterVariant: FilterType.SELECT,
        filterOptions: [
          { label: 'New', value: 'new' },
          { label: 'Renewal', value: 'renewal' },
          { label: 'Renewal Of Discontinued', value: 'renewalOfDiscontinued' },
        ],
      },
    }),
    columnHelper.accessor('role', {
      meta: {
        filterVariant: FilterType.SELECT,
        filterOptions: [
          { label: 'Team Assistant', value: 'teamAssistant' },
          { label: 'Engineer', value: 'engineer' },
        ],
      },
    }),
    columnHelper.accessor('status', {
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: Object.values(OfferStatus).map((status) => {
          return {
            label: camelToWords(status),
            value: status,
          }
        }),
      },
    }),

    columnHelper.accessor('SONumber', {
      header: () => <span>SAP Quotation#/SO #</span>,
      cell: RawTextCell,
    }),
  ]
  useEffect(() => {
    if (Object.keys(initialFilter).length > 0) {
      setStoredTableState((prev) => {
        return {
          ...tableInitialState,
          ...prev,
          ...initialFilter,
        }
      })

      navigate(`${location.pathname}`, { replace: true })
    }
  }, [initialFilter, navigate, setStoredTableState])

  if (!currentUser?.fullName) {
    return <>Loading...</>
  }

  return (
    <>
      <Box shadow="md" borderWidth="1px" bg="white" w="100%" mb="auto">
        <Flex p={5} borderBottom="1px solid #d8dbe0" gap="1">
          <Heading>My Offers Participation({offerParticipations?.count})</Heading>
          <Spacer></Spacer>
          <Button colorScheme={'green'} onClick={handleDownload}>
            Download
          </Button>
        </Flex>
        <Box p={5} overflow={'scroll'}>
          <FullTable
            data={offerParticipations?.data ? offerParticipations.data : []}
            rowCount={offerParticipations?.count}
            columnDef={defaultColumns}
            state={storedTableState}
            setState={setStoredTableState}
            handleRowClick={handleGotoOffer}
          />
        </Box>
      </Box>
    </>
  )
}

export default OfferParticipationListPage
